import { z } from "zod";
import { RingLimitsSchema } from "./leads";

// "tech" (Field Tech) sits below staff: their own assigned jobs, the time
// clock and field tools; no customer list, money documents, pricebook or
// admin screens. Office people are "staff". Firestore rules enforce it.
export const RoleSchema = z.enum(["owner", "admin", "manager", "staff", "tech"]);
export type Role = z.infer<typeof RoleSchema>;

export const ROLE_RANK: Record<Role, number> = {
  owner: 4,
  admin: 3,
  manager: 2,
  staff: 1,
  tech: 0,
};

export const ROLE_LABEL: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  manager: "Manager",
  staff: "Office staff",
  tech: "Field tech",
};

export const BillingStatusSchema = z.enum([
  "comped",
  "trial",
  "active",
  "past_due",
  "suspended",
  "canceled",
]);
export type BillingStatus = z.infer<typeof BillingStatusSchema>;

export const DEFAULT_JOB_TYPES = ["Service", "Warranty", "Production", "Estimate"];

/** Job-page sections a job type's flow can hide from field techs. */
export const JOB_PAGE_SECTIONS = [
  { id: "work", label: "Found & done (parts and work)" },
  { id: "estimates", label: "Estimates" },
  { id: "payment", label: "Invoice & payment" },
  { id: "signoff", label: "Customer sign-off" },
  { id: "expenses", label: "Receipts" },
  { id: "agreements", label: "Agreements" },
  { id: "texts", label: "Texts to the customer" },
  { id: "photos", label: "Photos" },
  { id: "notes", label: "Field notes" },
] as const;
export type JobPageSection = (typeof JOB_PAGE_SECTIONS)[number]["id"];

export const TenantSchema = z.object({
  name: z.string().min(1),
  slug: z.string().min(2),
  contactName: z.string().default(""),
  contactEmail: z.string().email().or(z.literal("")).default(""),
  contactPhone: z.string().default(""),
  /** The company logo (Storage path, and its public link) for documents and customer pages. */
  logoPath: z.string().optional(),
  logoUrl: z.string().optional(),
  /** Printed under the company's name on estimates, invoices and agreements (license numbers, bid limits). Off when the logo already carries them. */
  licenseLines: z.string().max(300).default(""),
  printLicenseLines: z.boolean().default(true),
  billingStatus: BillingStatusSchema.default("comped"),
  trialEndsAt: z.number().nullable().default(null),
  planId: z.string().nullable().default(null),
  // Vertical the company works in; drives module defaults and vocabulary
  // (see presets.ts). null = pre-presets tenant, treated as "general".
  industry: z.string().nullable().default(null),
  featureFlags: z.record(z.boolean()).default({}),
  /** Clevis Ring caps for this company (super admin); unset or null = no limit. */
  ring: RingLimitsSchema.optional(),
  // Owner's choice to let managers see and work the email-intake queue.
  // Mailbox + rule setup stays admin/owner regardless.
  intakeShared: z.boolean().default(false),
  // Send estimates to QuickBooks (an admin's choice on the QuickBooks page).
  qbSendEstimates: z.boolean().default(false),
  // The customer's sign-off when a job is finished: never asked, offered, or
  // required before "Finish job".
  jobSignoff: z.enum(["off", "optional", "required"]).default("optional"),
  // The call-taker's one-tap job types (Jobs → Job settings). Unset means
  // DEFAULT_JOB_TYPES; a picked type starts the job title ("Service - …").
  jobTypes: z.array(z.string().min(1).max(40)).max(30).optional(),
  // How each job type pays the tech by default: hourly (time clock) or piece
  // rate, plus a flat piece amount per job of that type.
  jobTypePay: z
    .record(z.string(), z.object({ mode: z.enum(["hourly", "piece"]), flatCents: z.number().int().min(0).max(10_000_000).default(0) }))
    .optional(),
  /** Two techs on a piece-rate job: split the pieces evenly, or each earns them in full. */
  pieceSplit: z.enum(["even", "each"]).optional(),
  /** Parts a tech logs beyond the job's plan: paid as pieces right away, or only after a manager's OK. */
  pieceExtras: z.enum(["auto", "approve"]).optional(),
  // Flows: per job type, the job-page sections field techs DON'T get (an
  // install crew needn't see payment). Enforced in the tech callables too.
  jobTypeFlows: z.record(z.string(), z.array(z.string())).optional(),
  // Workflows: per job type, which steps the tech gets (on my way, partial
  // complete, dry run) and what each asks for. See workflows.ts.
  jobTypeWorkflows: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
  createdAt: z.number(),
  archivedAt: z.number().nullable().default(null),
});
export type Tenant = z.infer<typeof TenantSchema>;

export const LocationSchema = z.object({
  name: z.string().min(1),
  addressLine1: z.string().default(""),
  addressLine2: z.string().default(""),
  city: z.string().default(""),
  state: z.string().default(""),
  zip: z.string().default(""),
  lat: z.number().nullable().default(null),
  lng: z.number().nullable().default(null),
  archivedAt: z.number().nullable().default(null),
  createdAt: z.number(),
});
export type Location = z.infer<typeof LocationSchema>;

/** A team member's home address. Private, like pay: on /users, never on the member mirror. */
export const HomeAddressSchema = z.object({
  line1: z.string().max(200).default(""),
  line2: z.string().max(200).default(""),
  city: z.string().max(120).default(""),
  state: z.string().max(60).default(""),
  zip: z.string().max(20).default(""),
});
export type HomeAddress = z.infer<typeof HomeAddressSchema>;
/** "12 Elm St, Apt 4, Henderson, NV 89002" (empty parts left out). */
export const formatHomeAddress = (a: Partial<HomeAddress> | null | undefined): string =>
  a ? [a.line1, a.line2, [a.city, [a.state, a.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")].filter((x) => x && x.trim()).join(", ") : "";

// Mirror of a user's membership. Authoritative role/tenant live in Auth custom claims;
// functions keep this doc and the claims in sync in the same operation.
export const UserDocSchema = z.object({
  tenantId: z.string(),
  role: RoleSchema,
  displayName: z.string().default(""),
  email: z.string().email(),
  locationIds: z.array(z.string()).default([]), // empty = all locations
  disabled: z.boolean().default(false),
  hourlyRateCents: z.number().int().nonnegative().nullable().default(null),
  /** Commission on what they sell, in basis points (500 = 5%). Admin-only, like pay. */
  commissionRateBps: z.number().int().min(0).max(10000).nullable().optional(),
  // How far this person will drive for off-shift work (available-tech dispatch).
  offShiftRadiusKm: z.number().int().positive().max(500).nullable().default(null),
  /** Home address (admins only; from a team import or the Team page). */
  homeAddress: HomeAddressSchema.nullable().optional(),
  /** When they got this role in this company (two-step sign-in grace for new owners/admins). */
  roleSince: z.number().optional(),
  createdAt: z.number(),
});
export type UserDoc = z.infer<typeof UserDocSchema>;

export const InviteSchema = z.object({
  tenantId: z.string(),
  email: z.string().email(),
  role: RoleSchema,
  locationIds: z.array(z.string()).default([]),
  invitedByUid: z.string(),
  createdAt: z.number(),
  expiresAt: z.number(),
  /** Last time the invite email went out (imports create invites without emailing). */
  lastEmailedAt: z.number().nullable().default(null),
  // From a team import: applied when the person accepts.
  displayName: z.string().max(120).optional(),
  phone: z.string().max(40).optional(),
  title: z.string().max(120).optional(),
  payrollId: z.string().max(60).optional(),
  departmentIds: z.array(z.string()).max(20).optional(),
  hourlyRateCents: z.number().int().nonnegative().max(100000000).nullable().optional(),
  homeAddress: HomeAddressSchema.optional(),
  /** Where it came from ("Team import: gusto-export.csv"). */
  source: z.string().max(200).optional(),
});
export type Invite = z.infer<typeof InviteSchema>;
